import { useState, useMemo } from 'react'
import {
  ActionIcon,
  Badge,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconFlask, IconSearch, IconX } from '@tabler/icons-react'
import { Navigation } from './components'
import { useTestCatalog } from '../Patient/components/OrderTests/shared/useTestCatalog'
import type { CatalogTest } from '../Patient/components/OrderTests/shared/useTestCatalog'
import { useDebounce } from '@/hooks/useDebounce'

const VENDORS = [
  { value: 'labcorp', label: 'Labcorp' },
  { value: 'quest', label: 'Quest Diagnostics' },
  { value: 'accureference', label: 'AccuReference' },
  { value: 'gti', label: 'GTI' },
]

export function TestCatalog() {
  const [vendor, setVendor] = useState('labcorp')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  // Fetch all tests for the selected vendor (when no search) or search results
  const {
    tests: allTests,
    loading: allLoading,
    total: allTotal,
  } = useTestCatalog(vendor, {
    _count: 5000,
    enabled: !debouncedSearch.trim(),
  })

  const { tests: searchResults, loading: searchLoading } = useTestCatalog(vendor, {
    q: debouncedSearch,
    _count: 100,
    enabled: debouncedSearch.trim().length >= 2,
  })

  const isSearching = debouncedSearch.trim().length >= 2
  const tests = isSearching ? searchResults : allTests
  const loading = isSearching ? searchLoading : allLoading

  // Group tests by category for summary
  const categorySummary = useMemo(() => {
    const map = new Map<string, { display: string; count: number; favorites: number }>()
    for (const t of allTests) {
      const cat = t.category || 'other'
      const existing = map.get(cat) || { display: t.categoryDisplay || cat, count: 0, favorites: 0 }
      existing.count++
      if (t.isFavorite) existing.favorites++
      map.set(cat, existing)
    }
    return [...map.entries()].sort((a, b) => b[1].count - a[1].count)
  }, [allTests])

  return (
    <>
      <Navigation />
      <div className="px-6 py-4">
        <Title order={2} mb="md">
          Test Catalog
        </Title>

        <Stack gap="md" maw={1100}>
          <Group align="flex-end" gap="md">
            <Select
              label="Vendor"
              data={VENDORS}
              value={vendor}
              onChange={(v) => {
                if (v) {
                  setVendor(v)
                  setSearch('')
                }
              }}
              w={220}
            />
            <TextInput
              label="Search"
              placeholder="Search by test name or code..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1"
              rightSection={
                search ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              rightSectionPointerEvents={search ? 'all' : 'none'}
            />
          </Group>

          {/* Category summary */}
          {!isSearching && !allLoading && (
            <Paper withBorder p="md">
              <Group gap="xs" mb="sm">
                <IconFlask size={18} />
                <Text fw={600}>
                  {allTotal} tests across {categorySummary.length} categories
                </Text>
              </Group>
              <Group gap="xs" wrap="wrap">
                {categorySummary.map(([cat, info]) => (
                  <Badge
                    key={cat}
                    variant="light"
                    size="lg"
                    leftSection={
                      <Text size="xs" fw={700}>
                        {info.count}
                      </Text>
                    }
                  >
                    {info.display}
                    {info.favorites > 0 && (
                      <span className="ml-1 text-xs text-gray-400">({info.favorites} fav)</span>
                    )}
                  </Badge>
                ))}
              </Group>
            </Paper>
          )}

          {/* Loading */}
          {loading && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading tests...
              </Text>
            </Group>
          )}

          {/* Search hint */}
          {isSearching && debouncedSearch.length < 2 && (
            <Text size="sm" c="dimmed" ta="center" py="sm">
              Type at least 2 characters to search.
            </Text>
          )}

          {/* Results table */}
          {!loading && tests.length > 0 && (
            <Paper withBorder>
              <Table withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={100}>Code</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th w={160}>Category</Table.Th>
                    <Table.Th w={100}>Specimen</Table.Th>
                    <Table.Th w={70}>Favorite</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tests.map((test: CatalogTest) => (
                    <Table.Tr key={test.id}>
                      <Table.Td>
                        <Text size="xs" className="font-mono">
                          {test.code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500}>
                          {test.name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" variant="light">
                          {test.categoryDisplay || test.category}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {test.specimen || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {test.isFavorite && (
                          <Badge size="xs" color="yellow">
                            Fav
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {isSearching && tests.length >= 100 && (
                <Text size="xs" c="dimmed" ta="center" py="xs">
                  Showing first 100 results. Refine your search.
                </Text>
              )}
            </Paper>
          )}

          {!loading && isSearching && tests.length === 0 && debouncedSearch.length >= 2 && (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              No tests found matching &quot;{search}&quot;.
            </Text>
          )}
        </Stack>
      </div>
    </>
  )
}
